import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { itemService } from '../../services/itemService';

/**
 * Names for the leading tag positions. Beyond these the position is shown by number,
 * since the deeper tags are a free hierarchy rather than a fixed scheme.
 */
const POSITION_LABELS = ['Grade', 'Domain', 'Strand', 'Topic', 'Subtopic', 'Detail'];

const labelFor = (index: number) => POSITION_LABELS[index] ?? `Tag ${index + 1}`;

/** Sentinel values, chosen so they cannot collide with a real tag. */
const NONE = '__none__';
const NEW = '__new__';

interface TagFieldsProps {
  tags: string[];
  onChange: (tags: string[]) => void;
}

/**
 * One dropdown per tag rather than a single comma-separated string.
 *
 * Options are the values already used at that position. "None" drops the tag and
 * "New…" captures one the vocabulary does not have yet, so the list stays closed at
 * the point of choosing while still allowing growth.
 */
const TagFields: React.FC<TagFieldsProps> = ({ tags, onChange }) => {
  const [vocabulary, setVocabulary] = useState<string[][]>([]);
  const [newTagIndex, setNewTagIndex] = useState<number | null>(null);
  const [newTagValue, setNewTagValue] = useState('');

  useEffect(() => {
    let cancelled = false;
    itemService
      .getTagVocabulary()
      .then((values) => {
        if (!cancelled) setVocabulary(values);
      })
      .catch(() => {
        // A missing vocabulary only costs the suggestions; New still works.
        if (!cancelled) setVocabulary([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Known values at this position, plus the current one when it is not among them. */
  const optionsFor = (index: number, current: string) => {
    const known = vocabulary[index] ?? [];
    return current && !known.includes(current) ? [current, ...known] : known;
  };

  const handleSelect = (index: number, value: string) => {
    if (value === NEW) {
      setNewTagValue('');
      setNewTagIndex(index);
      return;
    }
    if (value === NONE) {
      onChange(tags.filter((_, i) => i !== index));
      return;
    }
    const next = [...tags];
    next[index] = value;
    onChange(next);
  };

  const confirmNewTag = () => {
    const value = newTagValue.trim();
    if (value && newTagIndex !== null) {
      const next = [...tags];
      next[newTagIndex] = value;
      onChange(next);
    }
    setNewTagIndex(null);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Tags
      </Typography>

      {tags.map((tag, index) => (
        <TextField
          key={index}
          select
          fullWidth
          size="small"
          label={labelFor(index)}
          value={tag || ''}
          onChange={(event) => handleSelect(index, event.target.value)}
          sx={{ mb: 1.5 }}
          // Long topic names would otherwise be clipped mid-word.
          SelectProps={{ sx: { '& .MuiSelect-select': { whiteSpace: 'normal' } } }}
        >
          <MenuItem value={NONE}>
            <em>None</em>
          </MenuItem>
          {optionsFor(index, tag).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
          <MenuItem value={NEW}>
            <em>New…</em>
          </MenuItem>
        </TextField>
      ))}

      {tags.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No tags yet
        </Typography>
      )}

      <Button size="small" startIcon={<Add />} onClick={() => onChange([...tags, ''])}>
        Add tag
      </Button>

      <Dialog open={newTagIndex !== null} onClose={() => setNewTagIndex(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New {newTagIndex !== null ? labelFor(newTagIndex).toLowerCase() : 'tag'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={newTagValue}
            onChange={(event) => setNewTagValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                confirmNewTag();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTagIndex(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmNewTag} disabled={!newTagValue.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagFields;
